import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useSite } from '../../contexts/SiteContext';
import { tierLinesIn } from '../../data/gifts';
import { freeShippingLine, policyCopy } from '../../data/policies';
import { ease } from '../../core/format/motion';

/** Zo lang staat elke melding in beeld (telefoon). Rustig genoeg om uit te lezen. */
const ROTATE_MS = 5000;

/**
 * De regel boven de header, op klei (de diepte van de site, nooit zwart). Afgeleid van de
 * verzend- en ruilregels en de cadeaustaffel: nooit handmatig bijhouden.
 *
 * Computer: alle meldingen tegelijk, stil, gescheiden door een haarlijn. Niets beweegt.
 * Telefoon: één melding tegelijk die rustig wisselt; pauzeert bij aanraken en focus
 * (WCAG 2.2.2) en staat stil bij "minder beweging". Shell op klei = 5,6:1.
 */
export function AnnouncementBar() {
  const {
    policy,
    giftTiers
  } = useSite();
  const shippingLine = freeShippingLine(policy.freeShippingFrom);
  // `tierLinesIn()` levert de verzendregel zelf ook: dubbele regels eruit (ook als sleutel uniek).
  const announcements = [...new Set([shippingLine, policyCopy.delivery, ...tierLinesIn(giftTiers, policy.freeShippingFrom).filter(l => l !== shippingLine).slice(0, 1), policyCopy.returns])];
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useReducedMotion();
  const rotating = !reduced && !paused && announcements.length > 1;
  const current = announcements[tick % announcements.length] ?? '';
  useEffect(() => {
    if (!rotating) return;
    const id = window.setInterval(() => setTick(t => t + 1), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [rotating]);
  return <div className="bg-clay text-shell"
  // Een eigen landmark: anders staat deze tekst buiten elke regio (axe `region`).
  role="region" aria-label="Aankondiging"
  // Geen live-regio: een schermlezer hoeft niet elke paar seconden onderbroken te worden.
  aria-live="off" onPointerEnter={() => setPaused(true)} onPointerLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-7815" data-mp="1zlm84geje59p">
      <div className="mx-auto flex h-9 max-w-content items-center justify-center overflow-hidden px-6 text-[13px] lg:px-10" data-id="element-7816" data-mp="16ymhdaf2cbhm">
        <ul className="hidden items-center gap-5 lg:flex" data-id="element-7817" data-mp="1azoenlpf6dgy">
          {announcements.slice(0, 3).map((a, i) => <li key={a} className="flex items-center gap-5 whitespace-nowrap" data-id="element-7818" data-mp="1yqtw25b0ddtd" data-mpi={i}>
              {i > 0 && <span aria-hidden="true" className="h-3 w-px bg-shell/40" data-id="element-7819" data-mp="23n24kzupa8rk" />}
              {a}
            </li>)}
        </ul>
        <div className="lg:hidden" data-id="element-7820" data-mp="1txsetqnbx26m">
          {reduced ? <p className="truncate" data-id="element-7821" data-mp="1tw7l238u9n9t">{current}</p> : <AnimatePresence mode="wait" initial={false} data-id="element-7822" data-mp="2hnd1okmlsjl6">
              <motion.p key={current} initial={{
            y: 8,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: -8,
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease
          }} className="truncate" data-id="element-7823" data-mp="2hls6qtwdmyms">
                {current}
              </motion.p>
            </AnimatePresence>}
        </div>
      </div>
    </div>;
}